import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaClipboardList, FaChevronDown } from 'react-icons/fa';

const CompactAssessmentSelector = ({ organizationId, selectedAssessment, onSelectAssessment }) => {
  const [assessments, setAssessments] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (organizationId) {
      fetchAssessments();
    }
    
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [organizationId]);

  const fetchAssessments = async () => {
    if (!organizationId) return;
    
    try {
      setLoading(true);
      const res = await axios.get(`/api/assessments?organizationId=${organizationId}`);
      // Filter to only show completed and submitted assessments
      const completedAssessments = res.data.filter(a => 
        a.status === 'completed' || a.status === 'submitted'
      );
      setAssessments(completedAssessments);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching assessments:', err);
      // If API call fails, provide some mock data for better UX
      const mockAssessments = [
        {
          _id: 'mock-assessment-1',
          title: "PAI's Universal Resilience Maturity Assessment",
          status: 'completed',
          score: 57,
          completedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString()
        },
        {
          _id: 'mock-assessment-2',
          title: "PAI's Financial Resilience Maturity Assessment",
          status: 'completed',
          score: 9,
          completedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString()
        }
      ];
      setAssessments(mockAssessments);
      setError('Using sample assessment data');
      setLoading(false);
    }
  };

  const handleSelectAssessment = (assessment) => {
    if (onSelectAssessment) {
      onSelectAssessment(assessment);
    }
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const styles = {
    container: {
      position: 'relative',
      marginRight: '15px',
      zIndex: 999
    },
    selector: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#FFFAFA',
      color: '#243444',
      border: '1px solid #D2BA7B',
      borderRadius: '4px',
      padding: '6px 12px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold',
      minWidth: '300px',
      justifyContent: 'space-between'
    },
    icon: {
      marginRight: '8px',
      fontSize: '16px'
    },
    chevron: {
      marginLeft: '8px',
      fontSize: '12px',
      transition: 'transform 0.3s ease'
    },
    chevronOpen: {
      transform: 'rotate(180deg)'
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      left: '0',
      backgroundColor: '#FFFAFA',
      border: '1px solid #D2BA7B',
      borderRadius: '4px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      width: '350px',
      maxHeight: '350px',
      overflowY: 'auto',
      zIndex: 1001,
      display: isOpen ? 'block' : 'none'
    },
    assessmentItem: {
      padding: '10px 15px',
      borderBottom: '1px solid #eee',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      display: 'flex',
      flexDirection: 'column'
    },
    assessmentTitle: {
      fontWeight: 'bold',
      color: '#243444',
      fontSize: '14px'
    },
    assessmentDate: {
      color: '#4C5454',
      fontSize: '12px',
      marginTop: '3px'
    },
    assessmentScore: {
      color: '#243444',
      fontSize: '13px',
      fontWeight: 'bold',
      marginTop: '5px'
    },
    activeAssessment: {
      backgroundColor: '#f0f0f0'
    },
    loading: {
      padding: '10px 15px',
      color: '#4C5454',
      textAlign: 'center'
    },
    noAssessments: {
      padding: '15px',
      color: '#4C5454',
      textAlign: 'center'
    }
  };

  return (
    <div style={styles.container} ref={dropdownRef} data-testid="assessment-selector">
      <div 
        style={styles.selector} 
        onClick={toggleDropdown}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FaClipboardList style={styles.icon} />
          <span style={{ maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedAssessment?.title || 'Select Assessment'}
          </span>
        </div>
        <FaChevronDown style={{
          ...styles.chevron,
          ...(isOpen ? styles.chevronOpen : {})
        }} />
      </div>
      
      <div style={styles.dropdown}>
        {loading ? (
          <div style={styles.loading}>Loading assessments...</div>
        ) : error ? (
          <div style={styles.loading}>{error}</div>
        ) : assessments.length === 0 ? (
          <div style={styles.noAssessments}>No completed assessments found</div>
        ) : (
          assessments.map(assessment => (
            <div
              key={assessment._id}
              style={{
                ...styles.assessmentItem,
                ...(selectedAssessment?._id === assessment._id ? styles.activeAssessment : {})
              }}
              onClick={() => handleSelectAssessment(assessment)}
            >
              <div style={styles.assessmentTitle}>{assessment.title}</div>
              <div style={styles.assessmentDate}>
                {formatDate(assessment.completedAt || assessment.updatedAt)}
              </div>
              <div style={styles.assessmentScore}>
                Score: {assessment.score ? (assessment.score < 1 ? Math.round(assessment.score * 100) : Math.round(assessment.score)) : 'N/A'}%
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CompactAssessmentSelector;
